import React, { useRef, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PaperPlaneRight, X } from '@phosphor-icons/react';

import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';

interface FeedbackReasonProps {
  open: boolean;
  onSubmit?: (reason: string) => void;
  thankYouText?: string;
  placeholder?: string;
  maxLength?: number;
}

export function FeedbackReason({
  open,
  onSubmit,
  thankYouText = 'Thank you for your feedback!',
  placeholder = 'What could we have done better? Your feedback is valuable to us...',
  maxLength = 500,
}: FeedbackReasonProps) {
  const [reason, setReason] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (open) {
      setReason('');
      setSubmitted(false);
      setTimeout(() => textareaRef.current?.focus(), 0);
    }
  }, [open]);

  const handleSubmit = () => {
    if (reason.trim()) {
      onSubmit?.(reason);
      setSubmitted(true);
    }
  };

  return (
    <AnimatePresence>
      {open && !submitted && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 12 }}
          transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
        >
          <Card className="gap-2 p-3">
            <CardContent className="px-0">
              <Textarea
                ref={textareaRef}
                placeholder={placeholder}
                className="w-full border-none shadow-none focus-visible:ring-0 p-0"
                maxLength={maxLength}
                value={reason}
                rows={4}
                onChange={(e) => setReason(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmit();
                  }
                }}
              />
              <div className="flex justify-end mt-1">
                <span className="text-xs text-muted-foreground select-none">
                  {maxLength - reason.length} characters remaining
                </span>
              </div>
            </CardContent>
            <CardFooter className="p-0 gap-2 flex-row-reverse">
              <Button size="icon" onClick={handleSubmit} disabled={!reason.length} aria-label="Submit feedback">
                <PaperPlaneRight aria-hidden="true" weight="bold" />
              </Button>
              <Button
                size="icon"
                variant="ghost"
                onClick={() => {
                  setReason('');
                  textareaRef.current?.focus();
                }}
                aria-label="Cancel feedback"
              >
                <X aria-hidden="true" weight="bold" />
              </Button>
            </CardFooter>
          </Card>
        </motion.div>
      )}
      {open && submitted && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 12 }}
          transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
        >
          <Card className="gap-2 p-3">
            <CardContent className="px-0">
              <p>{thankYouText}</p>
            </CardContent>
          </Card>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
